import { useMemo } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useGetTrackingCardsQuery } from '../services/benefitApi';
const MONTH_TRANSLATION_KEYS = [
    'months.january',
    'months.february',
    'months.march',
    'months.april',
    'months.may',
    'months.june',
    'months.july',
    'months.august',
    'months.september',
    'months.october',
    'months.november',
    'months.december',
];
const getMonthKey = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
};
const getMonthName = (monthKey, t) => {
    const [, month] = monthKey.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    const monthTranslationKey = MONTH_TRANSLATION_KEYS[monthIndex];
    return t(monthTranslationKey);
};
const sortCardsByDate = (cards) => {
    return [...cards].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
};
const groupCardsByMonth = (cards, t) => {
    const sortedCards = sortCardsByDate(cards);
    const groupedByMonth = sortedCards.reduce((acc, card) => {
        const monthKey = getMonthKey(card.createdAt);
        if (!acc[monthKey]) {
            acc[monthKey] = [];
        }
        acc[monthKey].push(card);
        return acc;
    }, {});
    return Object.entries(groupedByMonth)
        .map(([monthKey, cards]) => ({
        month: getMonthName(monthKey, t),
        monthKey,
        cards,
    }))
        .sort((a, b) => b.monthKey.localeCompare(a.monthKey));
};
export function useTrackingCards({ contractId, enabled = true }) {
    const { t } = useTranslation();
    const shouldSkip = !contractId || !enabled;
    const { data, isLoading, isFetching, isError, refetch } = useGetTrackingCardsQuery({
        voucherContractId: contractId ?? '',
    }, {
        skip: shouldSkip,
    });
    const isLoadingOrFetching = isLoading || isFetching;
    const processedData = useMemo(() => {
        if (isLoadingOrFetching || isError || !data) {
            return {
                cardsByMonth: [],
                totalCards: 0,
            };
        }
        const cardsByMonth = groupCardsByMonth(data, t);
        const totalCards = data.length;
        return {
            cardsByMonth,
            totalCards,
        };
    }, [data, isLoadingOrFetching, isError, t]);
    return {
        cardsByMonth: processedData.cardsByMonth,
        totalCards: processedData.totalCards,
        isLoading: isLoadingOrFetching,
        isError,
        refetch,
    };
}
